import { memo, useCallback } from 'react'
import { Box, Checkbox, Radio, Typography } from '@mui/material'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import { QuestionnaireVersion, QueryMode } from '../types'
import { formatVersionDateRange } from '../utils/formatVersionDateRange'
import { useVersionListStyles } from '../styles/versionListStyles'

interface VersionListProps {
  versions: QuestionnaireVersion[]
  selectedVersionIds: string[]
  totalSelectedExecutions?: number
  queryMode: QueryMode
  onToggleVersion: (versionId: string) => void
  onToggleAllVersions?: () => void
  t: (key: string, options?: Record<string, unknown>) => string
}

const VersionList = ({
  versions,
  selectedVersionIds,
  totalSelectedExecutions,
  queryMode,
  onToggleVersion,
  onToggleAllVersions,
  t,
}: VersionListProps) => {
  const styles = useVersionListStyles()
  const isMulti = queryMode === 'one-unit-many-questionnaires'
  const SelectionControl = isMulti ? Checkbox : Radio

  const handleToggle = useCallback(
    (versionId: string) => {
      onToggleVersion(versionId)
    },
    [onToggleVersion]
  )

  const allSelected = versions.length > 0 && versions.every((v) => selectedVersionIds.includes(String(v.id)))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={styles.listColumnHeader}>
        <Typography>{t('dashboard.storePerformance.availableExecutions')}</Typography>
        <Typography sx={styles.listColumnCounter}>
          {t('dashboard.storePerformance.selectedExecutionCount', {
            count: totalSelectedExecutions ?? selectedVersionIds.length,
          })}
        </Typography>
      </Box>

      <Box sx={styles.listContainer}>
        {isMulti && onToggleAllVersions && versions.length > 1 && (
          <Box
            sx={styles.checkboxRowAll}
            onClick={onToggleAllVersions}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggleAllVersions()
              }
            }}
          >
            <Checkbox
              size="small"
              checked={allSelected}
              onChange={onToggleAllVersions}
              onClick={(e) => e.stopPropagation()}
              tabIndex={-1}
            />
            <Typography sx={{ fontWeight: 500, fontSize: '14px', color: styles.colors.color10 }}>
              {t('dashboard.storePerformance.selectAll')}
            </Typography>
          </Box>
        )}

        {versions.map((version) => {
          const versionId = String(version.id)
          const checked = selectedVersionIds.includes(versionId)
          const { dateText, timeText } = formatVersionDateRange(
            version.firstScheduledDate,
            version.lastScheduledDate,
            t
          )

          return (
            <Box
              key={versionId}
              sx={styles.listItemRow}
              onClick={() => handleToggle(versionId)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleToggle(versionId)
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, pl: '4px', pr: '8px' }}>
                <SelectionControl
                  size={isMulti ? 'small' : 'medium'}
                  checked={checked}
                  onChange={() => handleToggle(versionId)}
                  onClick={(e) => e.stopPropagation()}
                  tabIndex={-1}
                  sx={!isMulti ? styles.selectionControl : undefined}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: styles.colors.color22,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {version.name}
                  </Typography>
                  {(dateText || timeText) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '2px' }}>
                      {dateText && (
                        <>
                          <CalendarTodayOutlinedIcon sx={{ fontSize: '12px', color: styles.colors.color9 }} />
                          <Typography sx={{ fontSize: '13px', color: styles.colors.color9, whiteSpace: 'nowrap' }}>
                            {dateText}
                          </Typography>
                        </>
                      )}
                      {dateText && timeText && (
                        <Typography sx={{ fontSize: '13px', color: styles.colors.color9 }}>•</Typography>
                      )}
                      {timeText && (
                        <>
                          <AccessTimeOutlinedIcon sx={{ fontSize: '12px', color: styles.colors.color9 }} />
                          <Typography sx={{ fontSize: '13px', color: styles.colors.color9, whiteSpace: 'nowrap' }}>
                            {timeText}
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )
        })}

        {versions.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: styles.colors.color9 }}>
              {t('general.ninformationNotFound')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

const VersionListMemo = memo(VersionList)
export { VersionListMemo as VersionList }
